import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { S_TextNumberValue } from 'elements/minute/sidebar/sidebarComponents/atomic/TextNumberValue/TextNumberValue';
import { useMapheimDebounced } from 'hooks/general/useMapheimDebounced';
import { EFonts, EVariables } from 'libs/styles/styling';
import { useAnalytics } from 'libs/utils/site/analytics';
import { CSSProperties, useContext, useEffect, useRef, useState } from 'react';
import { CtxEventView } from 'session/ctxEventView';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

interface Props {
    value?: string;
    width?: string;
    autoFocus?: boolean;
    onChange?: (value: string) => void;
    onBlur?: (value: string) => void;
    onFocus?: () => void;
    placeholder?: string;
    className?: string;
    style?: CSSProperties;
    clearButton?: boolean;
}

export const SearchInput = (props: Props): JSX.Element => {
    const [value, setValue] = useState<string>(props.value ?? '');
    const ref = useRef<HTMLInputElement>(null);
    const analytics = useAnalytics();

    const { isWide } = useContext(CtxEventView);

    const sendAnalytics = useMapheimDebounced(
        () => analytics.track('event_ui.entity_search.changed'),
        2000
    );

    useEffect(() => {
        if (props.value !== value) {
            setValue(props.value);
        }
    }, [props.value]);

    return (
        <S_SearchValue className={props.className} $width={props.width}>
            <S_Icon_Glass icon={['far', 'magnifying-glass']} />
            <S_Input
                ref={ref}
                style={props.style}
                type="text"
                value={value ?? ''}
                placeholder={props.placeholder ?? Localization.SEARCH}
                autoComplete="nope"
                autoFocus={props.autoFocus}
                onChange={(e) => {
                    setValue(e.target.value);
                    props.onChange?.(e.target.value);

                    sendAnalytics();
                }}
                onBlur={() => {
                    props.onBlur?.(value);
                }}
                onFocus={() => {
                    props.onFocus?.();
                }}
                onKeyDown={(e) => {
                    e.stopPropagation();
                }}
                $isWide={isWide}
            />
            {props.clearButton && value && (
                <S_Icon_Clear
                    icon={['far', 'xmark']}
                    onClick={() => {
                        setValue('');
                        props.onChange?.('');
                    }}
                />
            )}
        </S_SearchValue>
    );
};

const S_SearchValue = styled.div<{ $width?: string }>`
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    display: flex;
    width: ${(props) => props.$width ?? '100%'};
`;

const S_Icon_Glass = styled(FontAwesomeIcon)`
    color: #041321;
    position: absolute;
    left: 20px;
    font-size: 16px;
`;

const S_Icon_Clear = styled(FontAwesomeIcon)`
    color: #041321;
    position: absolute;
    right: 20px;
    font-size: 16px;
`;

const S_Input = styled(S_TextNumberValue)<{ $width?: string; $isWide: boolean }>`
    outline: none;
    border-top: none;
    border-left: none;
    border-right: none;
    box-sizing: border-box;
    padding: 0px 10px 0 50px;
    border-radius: 7px;
    background: #fafcfe;
    color: #041321;
    font-family: ${EFonts.Medium};
    width: ${(props) => (props.width ? props.width : '100%')};
    font-size: 15px;
    border: 1px solid #eaf0f5;
    height: 44px;
    text-align: center;
    border-radius: ${EVariables.Radius1};
    box-shadow: none;
    font-family: ${EFonts.InterRegular};
    line-height: 44px;
    &:focus {
        background: #fff;
    }
    &::placeholder {
        color: #646464;
        font-size: 15px;
        padding: 0px;
        text-align: left;
        font-family: ${EFonts.InterRegular};
        line-height: 44px;
    }
    text-align: left;
    width: 100%;
    ${(props) => {
        if (props.$isWide) {
            return css`
                padding: 0 10px 0 50px;
                height: 53px;
                line-height: 53px;
            `;
        }
    }}
`;
